import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LengthsEditor } from "@/components/materials/LengthsEditor";
import { isZip } from "@/lib/zips";

export const ACCESSORY_TYPES = ["bottone", "cerniera", "filato", "etichetta", "fodera", "altro"];
const ACC_UNITS = ["pezzi", "metri", "rocche"];

const Field = ({ label, span, children }) => (
  <div className={`space-y-1.5 ${span ? "col-span-2" : ""}`}><Label>{label}</Label>{children}</div>
);

const YesNo = ({ value, onChange, testid = "tessuti-field-fusible" }) => (
  <div className="flex gap-1 rounded-md border border-border p-0.5" data-testid={testid}>
    {[[false, "No"], [true, "Sì"]].map(([v, l]) => (
      <button key={l} type="button" onClick={() => onChange(v)} className={`h-8 flex-1 rounded text-sm transition-colors ${!!value === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`} data-testid={`${testid}-${v ? "si" : "no"}`}>{l}</button>
    ))}
  </div>
);

// Tessuto con cui viene ricoperto il bottone: il colore arriva dal tessuto
export const FabricPick = ({ value, onChange, testid }) => {
  const { data: all = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const fabrics = all.filter((m) => m.type === "tessuto").sort((a, b) => `${a.name} ${a.color}`.localeCompare(`${b.name} ${b.color}`));
  return (
    <Select value={value || undefined} onValueChange={(id) => onChange(fabrics.find((m) => m.id === id))}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Scegli il tessuto" /></SelectTrigger>
      <SelectContent className="max-h-72">{fabrics.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.color || "-"}{m.season ? ` (${m.season})` : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
};

const Pick = ({ value, options, onChange, placeholder, testid, capitalize }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className={capitalize ? "capitalize" : ""}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {/* Mantiene visibile un valore vecchio non più presente nell'elenco */}
      {value && !options.includes(value) && <SelectItem value={value}>{value}</SelectItem>}
      {options.map((t) => <SelectItem key={t} value={t} className={capitalize ? "capitalize" : ""}>{t}</SelectItem>)}
    </SelectContent>
  </Select>
);

// mode: "new" = scheda + prima variante, "variant" = solo dati variante, "group" = solo dati comuni
export const MaterialForm = ({ kind, form, set, mode = "new", lists, seasons = [] }) => {
  const inp = (k, type = "text") => (
    <Input type={type} value={form[k] ?? ""} onChange={(e) => set({ ...form, [k]: e.target.value })} data-testid={`${kind}-field-${k}`} />
  );
  const pick = (k) => (v) => set({ ...form, [k]: v });
  const isButton = kind === "accessori" && /bottone/i.test(form.type || "");
  const cov = isButton && !!form.covered;
  const fabric = kind === "tessuti";
  const common = mode !== "variant";
  const variant = mode !== "group";
  return (
    <div className="grid grid-cols-2 gap-3 py-2">
      {common && fabric && (
        <>
          <Field label="Tipo di tessuto"><Pick value={form.fabric_type} options={lists.fabric_types} onChange={pick("fabric_type")} placeholder="Scegli il tipo" testid="tessuti-field-fabric_type" /></Field>
          {mode === "new" && <Field label="Stagione del tessuto"><Pick value={form.season} options={seasons} onChange={pick("season")} placeholder="Scegli stagione" testid="tessuti-field-season" /></Field>}
          <Field label="Nome dato dal fornitore">{inp("name")}</Field>
          <Field label="Composizione">{inp("composition")}</Field>
          <Field label="Può essere adesivato?"><YesNo value={form.fusible} onChange={pick("fusible")} testid="tessuti-field-fusible" /></Field>
        </>
      )}
      {common && !fabric && <Field label="Nome" span>{inp("name")}</Field>}
      {mode !== "variant" && isButton && <Field label="Ricoperto con un tessuto?" span={!cov}><YesNo value={form.covered} onChange={pick("covered")} testid="accessori-field-covered" /></Field>}
      {mode !== "variant" && cov && <Field label="Bottoni da 1 metro di tessuto">{inp("buttons_per_meter", "number")}</Field>}
      {mode === "group" && !fabric && (
        <>
          <Field label="Tipo"><Pick value={form.type} options={lists.accessory_types || []} onChange={pick("type")} placeholder="Scegli tipo" testid="accessori-field-type" capitalize /></Field>
          <Field label="Fornitore"><Pick value={form.supplier} options={lists.suppliers || []} onChange={pick("supplier")} placeholder={form.mixedSuppliers ? "Più fornitori: lascia invariato" : "Scegli fornitore"} testid="accessori-field-supplier" /></Field>
          <p className="col-span-2 text-[11px] text-muted-foreground">Tipo e fornitore vengono applicati a tutte le varianti di questo accessorio.</p>
        </>
      )}
      {mode === "group" && form.variants?.length > 0 && (
        <div className="col-span-2 space-y-1.5" data-testid={`${kind}-group-prices`}>
          <Label>{cov ? "Tessuto di copertura e " : ""}{fabric ? "costo al metro (€) per variante" : "costo unitario (€) per variante"}</Label>
          {form.variants.map((v) => (
            <div key={v.id} className="flex items-center gap-2">
              <span className="w-32 shrink-0 truncate text-sm text-muted-foreground">{v.color || "-"} · {v.supplier || "-"}</span>
              {cov && <div className="min-w-0 flex-1"><FabricPick value={form.covers[v.id]} onChange={(m) => set({ ...form, covers: { ...form.covers, [v.id]: m.id } })} testid={`accessori-cover-${v.id}`} /></div>}
              {!cov && <span className="flex-1" />}
              <Input type="text" inputMode="decimal" className="w-28 text-right" value={form.prices[v.id] ?? ""} onChange={(e) => set({ ...form, prices: { ...form.prices, [v.id]: e.target.value } })} data-testid={`${kind}-price-${v.id}`} />
            </div>
          ))}
        </div>
      )}
      {mode === "new" && !fabric && (
        <>
          <Field label="Tipo"><Pick value={form.type} options={lists.accessory_types || []} onChange={pick("type")} placeholder="Scegli tipo" testid="accessori-field-type" capitalize /></Field>
          <Field label="Unità"><Pick value={form.unit} options={ACC_UNITS} onChange={pick("unit")} testid="accessori-field-unit" /></Field>
        </>
      )}
      {variant && (
        <>
          {cov
            ? <Field label="Ricoperto con il tessuto (colore dal tessuto)"><FabricPick value={form.covered_fabric_id} onChange={(m) => set({ ...form, covered_fabric_id: m.id, color: m.color || "" })} testid="accessori-field-cover" /></Field>
            : <Field label="Colore"><Pick value={form.color} options={lists.colors} onChange={pick("color")} placeholder="Scegli colore" testid={`${kind}-field-color`} /></Field>}
          <Field label="Fornitore"><Pick value={form.supplier} options={lists.suppliers || []} onChange={pick("supplier")} placeholder={lists.suppliers?.length ? "Scegli fornitore" : "Aggiungilo in Gestisci elenchi"} testid={`${kind}-field-supplier`} /></Field>
          <Field label="Codice colore">{inp("supplier_code")}</Field>
          <Field label={fabric ? "Costo al metro (€)" : "Costo unitario (€)"}>{inp("cost_per_unit", "number")}</Field>
          {!fabric && isZip(form)
            ? <Field label="Giacenza per lunghezza (cerniera)" span><LengthsEditor value={form.lengths} onChange={(lengths) => set({ ...form, lengths })} testid="accessori-field-lengths" /></Field>
            : <Field label={fabric ? "Giacenza (metri)" : "Giacenza"} span>{inp("quantity", "number")}</Field>}
          {fabric && mode === "variant" && <Field label="Stagione" span><Pick value={form.season} options={seasons} onChange={pick("season")} placeholder="Scegli stagione" testid="tessuti-field-season" /></Field>}
        </>
      )}
    </div>
  );
};
